import { cx } from '@emotion/css';
import { HintProps } from '@phoenix/design-system/frontend/ui/form/hint/hint.types';
import { InputField, InputFieldProps } from '@phoenix/design-system/frontend/ui/form/input/input-field';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { useTranslation } from 'next-i18next';
import { FocusEvent, FormEvent, forwardRef, PropsWithChildren, Ref, useState } from 'react';
import { useFormContext } from 'react-hook-form';

const eyeIcon = getDesignSystemIcon('icon-eye-16.svg');
const eyeClosedIcon = getDesignSystemIcon('icon-eye-closed-16.svg');

const mergeRefs = (...refs: Array<Ref<HTMLInputElement> | undefined>) => {
  return (node: HTMLInputElement | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    });
  };
};

export const HookFormInput = forwardRef<
  HTMLInputElement,
  PropsWithChildren<
    InputFieldProps & {
      name: string;
      hintStrategy?: 'dirty' | 'touched' | 'dirtyOrTouched';
      validateOnSubmit?: boolean;
    }
  >
>(({ name, className, type = 'text', validateOnSubmit = false, hintStrategy = 'dirtyOrTouched', ...restProps }, ref) => {
  const {
    register,
    setValue,
    formState: { errors, dirtyFields, touchedFields },
    watch,
  } = useFormContext();
  const { t } = useTranslation('shared');
  const [inputType, setInputType] = useState(type);
  const icon = type === 'password' ? (inputType === 'password' ? eyeIcon : eyeClosedIcon) : restProps?.icon;

  const isDirtyOrTouched = (fieldName: string) => {
    if (validateOnSubmit) {
      return true;
    }
    if (hintStrategy === 'dirty') {
      return dirtyFields[fieldName];
    }
    if (hintStrategy === 'touched') {
      return touchedFields[fieldName];
    }
    return dirtyFields[fieldName] || touchedFields[fieldName];
  };

  const value = watch(name);

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    const {
      value,
      validity: { valid },
    } = e.currentTarget;
    restProps.onBlur?.(e);
    setValue(name, valid && value === '' ? null : value, { shouldValidate: true, shouldTouch: true });
  };

  const onChange = (e: FormEvent<HTMLInputElement>) => {
    const {
      value,
      validity: { valid },
    } = e.currentTarget;
    restProps.onChange?.(e);
    setValue(name, valid && value === '' ? null : value, { shouldValidate: !validateOnSubmit, shouldDirty: true });
  };

  const hint: HintProps | undefined = errors[name]
    ? {
        message: errors[name]?.message,
        type: 'error',
        dataTestId: `${name}ValidationErrorMessage`,
      }
    : undefined;

  const registerResult = register(name);
  const { ref: registerRef = null, ...registerRest } = registerResult || {};

  return (
    <InputField
      hint={isDirtyOrTouched(name) ? hint : undefined}
      iconClickAction={() => {
        if (type === 'password') {
          setInputType((prevType) => (prevType === 'password' ? 'text' : 'password'));
        }
      }}
      icon={icon}
      type={inputType}
      className={cx(className, 'mb-4 lg:mb-m', { hidden: type === 'hidden' })}
      classNameInput={cx(restProps.classNameInput, { '!text-secondary': type === 'date' && !value })}
      {...restProps}
      label={restProps.label ? `${restProps.label} ${!restProps.required ? t('shared_formControlLabel_optionalSuffix') : ''}` : undefined}
      charsCounter={value?.length}
      {...(type !== 'date'
        ? registerRest
        : {
            ...registerRest,
            onBlur,
            onChange,
          })}
      ref={mergeRefs(registerRef, ref)}
    />
  );
});

HookFormInput.displayName = 'HookFormInput';
